import { FC, useEffect, useMemo, useState } from 'react';
import Modal from '@/components/Modal/Modal';
import LoadingSpinner from '@/components/LoadingSpinner/LoadingSpinner';
import useGetUserProfile from '@/services/user/queries/useGetUserProfile';
import CloseModalButton from '@/components/CloseModalButton/CloseModalButton';
import ReactCountryFlag from 'react-country-flag';
import { getName } from 'country-list';
import Avatar from '@/features/avatar/Avatar';
import NoteComponent from '@/features/player-notes/components/NoteComponent';
import { usePostNote } from '@/services/notes/queries/usePostNotes';
import { useDeleteNote } from '@/services/notes/queries/useDeleteNote';
import ColorPicker from '@/features/player-notes/components/ColorPicker';
import { usePatchNoteColor } from '@/services/notes/queries/usePatchNotesColor';
import { IoHelpCircleOutline } from 'react-icons/io5';
import { COLORS } from '@/constants/COLORS';
import ColorOption from '@/features/player-notes/components/ColorOption';
import CurrencyIcon, { TetherIconColorOptions } from '@/components/CurrencySymbolMask/CurrencyIcon';
import { ReactComponent as PhenomBadge } from '@/assets/phenom-badge.svg';
import useIsMobileView from '@/hooks/useIsMobileView';
import InputWithIcon from '@/components/InputWithIcon/InputWithIcon';
import { ReactComponent as PencilIcon } from '@/assets/icons/pencil-icon.svg';
import { twMerge } from 'tailwind-merge';

const BORDER_BOTTOM = { borderBottom: '1px solid #FFFFFF1A' };
const DEFAULT_COLOR = COLORS.purpleGrayDark;

interface PlayerNotesModalProps {
  userId: string;
  open: boolean;
  setClose: () => void;
  tableStack?: number;
}

const PlayerNotesModal: FC<PlayerNotesModalProps> = ({ userId, open, setClose, tableStack }) => {
  const [inputValue, setInputValue] = useState('');
  const isMobileView = useIsMobileView();
  const [showColorPicker, setShowColorPicker] = useState(!isMobileView);
  const maxLength = 100;

  const {
    data: profileData,
    isLoading: isLoadingProfile,
    refetch,
  } = useGetUserProfile({
    userId,
  });

  const { mutate, isSuccess } = usePostNote();
  const { mutate: mutateColor, isSuccess: isSuccessColorPatch } = usePatchNoteColor();

  const { mutate: mutateDeleteNote, isSuccess: isSuccessDeleteNote } = useDeleteNote();

  const playerNotes = useMemo(() => profileData?.data?.notes.reverse(), [profileData?.data?.notes]);

  useEffect(() => {
    if (isSuccess || isSuccessDeleteNote || isSuccessColorPatch) {
      refetch();
    }
  }, [isSuccess, isSuccessDeleteNote, refetch, isSuccessColorPatch]);

  useEffect(() => {
    if (isSuccessColorPatch && isMobileView) {
      setShowColorPicker(false);
    }
  }, [isSuccessColorPatch, isMobileView]);

  const handleDeleteNote = (noteId: string) => mutateDeleteNote({ id: noteId });

  const handleSelectColor = (value: string | null) => mutateColor({ userId, color: value });

  return (
    <Modal
      open={open}
      setClose={setClose}
      bodyClassName="overflow-y-auto w-full scrollbar-phenom"
      className={
        isMobileView
          ? 'h-full w-full'
          : 'max-h-[min(700px,calc(100vh-50px))] min-h-[min(700px,calc(100vh-50px))] min-w-[min(460px,calc(100vw-50px))] max-w-[min(460px,calc(100vw-50px))]'
      }
    >
      <div className={twMerge('absolute flex justify-end', isMobileView ? 'right-4' : 'right-8')}>
        <CloseModalButton onClose={setClose} />
      </div>
      <div className="grid grid-rows-[auto_auto_1fr] gap-8">
        {isLoadingProfile ? (
          <LoadingSpinner size="10" />
        ) : (
          <>
            <div className="flex justify-between pb-8" style={BORDER_BOTTOM}>
              <div className="flex gap-8">
                <Avatar
                  avatar2D
                  round={false}
                  className="h-15 w-15 rounded-lg border-2 border-ph-primary-violet"
                  userId={profileData?.data?.targetUserId || ''}
                />
                <div className="flex flex-col gap-1">
                  <p className="text-lg font-bold text-white">{profileData?.data.alias || 'Ambassador'}</p>

                  <p className="text-white">
                    <span className="flex flex-row items-center text-sm font-extrabold">
                      <CurrencyIcon fill={TetherIconColorOptions.white} />
                      {tableStack?.toString() || '0'}
                    </span>
                  </p>

                  {profileData?.data.isAmbassador && (
                    <div className="flex items-center gap-1">
                      <PhenomBadge className="h-5 w-5" />
                      <p className="text-sm font-bold text-white">
                        Phenom <span className="text-ph-purple">Team Pro</span>
                      </p>
                    </div>
                  )}
                </div>
              </div>
              {isMobileView && (
                <div className="mr-12">
                  <ColorOption
                    className="h-8 w-8"
                    color={profileData?.data.notesColor || DEFAULT_COLOR}
                    onClick={() => setShowColorPicker((prev) => !prev)}
                  >
                    {!profileData?.data.notesColor && <IoHelpCircleOutline color="#FFFFFF4D" />}
                  </ColorOption>
                </div>
              )}
            </div>
            <div className="flex flex-col gap-2 pb-8" style={BORDER_BOTTOM}>
              <div className="flex flex-col gap-2">
                {profileData?.data?.showRealNameEnabled && (
                  <div className="flex gap-4">
                    <span className="w-32 text-sm text-white text-opacity-50">Real Name</span>
                    <span className="text-sm text-white">
                      {profileData?.data.firstName || '******'} {profileData?.data.lastName || '******'}
                    </span>
                  </div>
                )}
                <div className="flex gap-4">
                  <span className="w-32 text-sm text-white text-opacity-50">Country</span>
                  <span className="text-sm">
                    <ReactCountryFlag countryCode={profileData?.data.countryCode || ''} />{' '}
                    {getName(profileData?.data.countryCode || '')}
                  </span>
                </div>
                <div className="flex gap-4">
                  <span className="w-32 text-sm text-white text-opacity-50">Connected from</span>
                  <div className="flex gap-1">
                    <span className="text-sm text-white">{profileData?.data.connectedFrom}</span>
                  </div>
                </div>
              </div>
              {showColorPicker && (
                <div className="flex min-h-[8.315em] flex-col justify-center gap-4">
                  <span className="text-sm text-white text-opacity-50">Player Color</span>
                  <ColorPicker onSelectedColor={handleSelectColor} selectedColor={profileData?.data.notesColor} />
                </div>
              )}
            </div>
          </>
        )}

        {isLoadingProfile ? (
          <LoadingSpinner size="10" />
        ) : (
          <div className="flex flex-col gap-4">
            <div className="flex flex-col gap-1">
              <InputWithIcon
                icon={<PencilIcon />}
                fullWidth
                maxLength={maxLength}
                placeholder="Write a note..."
                value={inputValue}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    mutate({ userId, noteContent: inputValue });
                    setInputValue('');
                  }
                }}
                onChange={(e) => {
                  setInputValue(e.target.value);
                }}
              />
              <span className="text-right text-sm text-white text-opacity-50">
                {inputValue.length}/{maxLength}
              </span>
            </div>
            <div className="scrollbar-phenom flex max-h-[12em] w-full flex-1 flex-col gap-2 overflow-y-auto">
              {playerNotes &&
                playerNotes.map((note) => {
                  return <NoteComponent note={note} key={note.id} onClickDelete={handleDeleteNote} />;
                })}
            </div>
          </div>
        )}
      </div>
    </Modal>
  );
};

export default PlayerNotesModal;
